import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const FileManagementSection = () => {
  const [catalogFile, setCatalogFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchCatalogFile();
  }, []);

  const fetchCatalogFile = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', 'catalog_file');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setCatalogFile(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching catalog file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);

      // First, delete the old file if it exists
      if (catalogFile?.url) {
        await deleteOldFile(catalogFile.url);
      }

      // Upload new file using the API
      const formData = new FormData();
      formData.append('project', 'KalaratansGifts');
      formData.append('type', 'CatalogPDF');
      formData.append('image', selectedFile);

      const uploadResponse = await fetch('https://imageapi.garanipublication.com/upload.php', {
        method: 'POST',
        body: formData
      });

      const uploadResult = await uploadResponse.json();

      if (uploadResult.success) {
        // Save file info to Firestore
        const fileData = {
          url: uploadResult.url,
          fileName: selectedFile.name,
          uploadDate: new Date().toISOString(),
          fileSize: selectedFile.size,
          fileType: 'application/pdf',
          downloadCount: 0,
          lastDownloaded: null
        };

        const docRef = doc(db, 'settings', 'catalog_file');
        await setDoc(docRef, fileData);

        setCatalogFile(fileData);
        setSelectedFile(null);
        alert('Catalog file uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteOldFile = async (fileUrl) => {
    try {
      const deleteResponse = await fetch('https://imageapi.garanipublication.com/delete.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `url=${encodeURIComponent(fileUrl)}`
      });

      const deleteResult = await deleteResponse.json();
      return deleteResult.success;
    } catch (error) {
      console.error('Error deleting old file:', error);
      return false;
    }
  };

  const handleDeleteFile = async () => {
    if (!catalogFile?.url) return;

    const confirmed = window.confirm('Are you sure you want to delete the current catalog file?');
    if (!confirmed) return;

    try {
      setLoading(true);

      // Delete from API
      const success = await deleteOldFile(catalogFile.url);
      
      if (success) {
        // Remove from Firestore
        const docRef = doc(db, 'settings', 'catalog_file');
        await updateDoc(docRef, {
          url: null,
          fileName: null,
          uploadDate: null,
          fileSize: null,
          fileType: null
        });

        setCatalogFile(null);
        alert('Catalog file deleted successfully!');
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Catalog File Management</h2>
        <p className="text-gray-600">Manage the PDF catalog file that users download after form submission</p>
      </div>

      {/* Current File Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Catalog File</h3>
        
        {loading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ) : catalogFile?.url ? (
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900">{catalogFile.fileName}</h4>
                <p className="text-sm text-gray-500">Size: {formatFileSize(catalogFile.fileSize)}</p>
                <p className="text-sm text-gray-500">Uploaded: {formatDate(catalogFile.uploadDate)}</p>
                <p className="text-sm text-gray-500">Downloads: {catalogFile.downloadCount || 0}</p>
                {catalogFile.lastDownloaded && (
                  <p className="text-sm text-gray-500">Last Downloaded: {formatDate(catalogFile.lastDownloaded)}</p>
                )}
                <a 
                  href={catalogFile.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  View File
                </a>
              </div>
            </div>
            <button
              onClick={handleDeleteFile}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
            >
              Delete File
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No catalog file uploaded</h3>
            <p className="mt-1 text-sm text-gray-500">Upload a PDF file for users to download</p>
          </div>
        )}
      </div>

      {/* Upload New File */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {catalogFile?.url ? 'Replace Catalog File' : 'Upload Catalog File'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select PDF File
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {selectedFile && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Selected File:</h4>
              <p className="text-sm text-gray-600">Name: {selectedFile.name}</p>
              <p className="text-sm text-gray-600">Size: {formatFileSize(selectedFile.size)}</p>
              <p className="text-sm text-gray-600">Type: {selectedFile.type}</p>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={uploadFile}
              disabled={!selectedFile || uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </div>
              ) : (
                catalogFile?.url ? 'Replace File' : 'Upload File'
              )}
            </button>
            
            {selectedFile && (
              <button
                onClick={() => setSelectedFile(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Instructions</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Only PDF files are accepted</li>
                <li>Recommended file size: Less than 10MB for faster downloads</li>
                <li>When you upload a new file, the old file will be automatically deleted</li>
                <li>Users will download this file after submitting contact/quotation forms</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileManagementSection;
